/** State columns store two-character codes. */
export function stateCodeMaxLength(): number {
  return 2;
}

const US_STATES: Record<string, string> = {
  alabama: 'AL',
  alaska: 'AK',
  arizona: 'AZ',
  arkansas: 'AR',
  california: 'CA',
  colorado: 'CO',
  connecticut: 'CT',
  delaware: 'DE',
  'district of columbia': 'DC',
  florida: 'FL',
  georgia: 'GA',
  hawaii: 'HI',
  idaho: 'ID',
  illinois: 'IL',
  indiana: 'IN',
  iowa: 'IA',
  kansas: 'KS',
  kentucky: 'KY',
  louisiana: 'LA',
  maine: 'ME',
  maryland: 'MD',
  massachusetts: 'MA',
  michigan: 'MI',
  minnesota: 'MN',
  mississippi: 'MS',
  missouri: 'MO',
  montana: 'MT',
  nebraska: 'NE',
  nevada: 'NV',
  'new hampshire': 'NH',
  'new jersey': 'NJ',
  'new mexico': 'NM',
  'new york': 'NY',
  'north carolina': 'NC',
  'north dakota': 'ND',
  ohio: 'OH',
  oklahoma: 'OK',
  oregon: 'OR',
  pennsylvania: 'PA',
  'puerto rico': 'PR',
  'rhode island': 'RI',
  'south carolina': 'SC',
  'south dakota': 'SD',
  tennessee: 'TN',
  texas: 'TX',
  utah: 'UT',
  vermont: 'VT',
  virginia: 'VA',
  'virgin islands': 'VI',
  washington: 'WA',
  'west virginia': 'WV',
  wisconsin: 'WI',
  wyoming: 'WY',
};

const CA_PROVINCES: Record<string, string> = {
  alberta: 'AB',
  'british columbia': 'BC',
  manitoba: 'MB',
  'new brunswick': 'NB',
  'newfoundland and labrador': 'NL',
  'northwest territories': 'NT',
  'nova scotia': 'NS',
  nunavut: 'NU',
  ontario: 'ON',
  'prince edward island': 'PE',
  quebec: 'QC',
  québec: 'QC',
  saskatchewan: 'SK',
  yukon: 'YT',
};

// Every state column in the system is NVARCHAR(2) (CreditApplication.BillingState,
// Customer.PhysicalState, Customer.MailingState), so ISO 3166-2 codes such as
// "CMX" do not fit. Correos de México publishes no official abbreviation list;
// the 2-letter column below is the commercial convention used by CBP, UPS and
// PC*Miler. Where that list offers several codes we take the one that does not
// collide: Chiapas CS and Coahuila CO avoid the shared "CH"; Chihuahua uses CH.
// Tabasco TB and Tamaulipas TM avoid the shared "TA". Mexico City maps to the
// historical "DF", the only 2-letter code in common use for it.
//
// Keys are normalised (lower case, accents/dots/spaces stripped, "MX-" removed)
// and cover what Google actually returns: conventional abbreviations such as
// "Jal." and "N.L.", full Spanish names, ISO suffixes, and the code itself.
const MX_STATES: Record<string, string> = {
  aguascalientes: 'AG',
  ags: 'AG',
  agu: 'AG',
  ag: 'AG',
  bajacalifornia: 'BC',
  bc: 'BC',
  bcn: 'BC',
  bj: 'BC',
  bn: 'BC',
  bajacaliforniasur: 'BS',
  bcs: 'BS',
  bs: 'BS',
  campeche: 'CM',
  camp: 'CM',
  cam: 'CM',
  cm: 'CM',
  cp: 'CM',
  chiapas: 'CS',
  chis: 'CS',
  chp: 'CS',
  cs: 'CS',
  chihuahua: 'CH',
  chih: 'CH',
  chh: 'CH',
  ci: 'CH',
  coahuila: 'CO',
  coahuiladezaragoza: 'CO',
  coah: 'CO',
  coa: 'CO',
  co: 'CO',
  cu: 'CO',
  colima: 'CL',
  col: 'CL',
  cl: 'CL',
  ciudaddemexico: 'DF',
  cdmx: 'DF',
  cmx: 'DF',
  distritofederal: 'DF',
  df: 'DF',
  durango: 'DG',
  dgo: 'DG',
  dur: 'DG',
  dg: 'DG',
  guanajuato: 'GT',
  gto: 'GT',
  gua: 'GT',
  gj: 'GT',
  gt: 'GT',
  guerrero: 'GR',
  gro: 'GR',
  ge: 'GR',
  gr: 'GR',
  hidalgo: 'HG',
  hgo: 'HG',
  hid: 'HG',
  hd: 'HG',
  hg: 'HG',
  jalisco: 'JA',
  jal: 'JA',
  ja: 'JA',
  mexico: 'MX',
  estadodemexico: 'MX',
  edomex: 'MX',
  mex: 'MX',
  em: 'MX',
  michoacan: 'MI',
  michoacandeocampo: 'MI',
  mich: 'MI',
  mic: 'MI',
  mh: 'MI',
  mi: 'MI',
  morelos: 'MO',
  mor: 'MO',
  mo: 'MO',
  mr: 'MO',
  nayarit: 'NA',
  nay: 'NA',
  na: 'NA',
  nuevoleon: 'NL',
  nl: 'NL',
  nle: 'NL',
  oaxaca: 'OA',
  oax: 'OA',
  oa: 'OA',
  puebla: 'PU',
  pue: 'PU',
  pu: 'PU',
  queretaro: 'QT',
  qro: 'QT',
  que: 'QT',
  qa: 'QT',
  qe: 'QT',
  qt: 'QT',
  quintanaroo: 'QR',
  qroo: 'QR',
  roo: 'QR',
  qi: 'QR',
  qr: 'QR',
  sanluispotosi: 'SL',
  slp: 'SL',
  sl: 'SL',
  sinaloa: 'SI',
  sin: 'SI',
  si: 'SI',
  sonora: 'SO',
  son: 'SO',
  so: 'SO',
  tabasco: 'TB',
  tab: 'TB',
  tb: 'TB',
  tamaulipas: 'TM',
  tamps: 'TM',
  tam: 'TM',
  tm: 'TM',
  tlaxcala: 'TL',
  tlax: 'TL',
  tla: 'TL',
  tl: 'TL',
  veracruz: 'VE',
  veracruzdeignaciodelallave: 'VE',
  ver: 'VE',
  vc: 'VE',
  ve: 'VE',
  vl: 'VE',
  vz: 'VE',
  yucatan: 'YU',
  yuc: 'YU',
  yc: 'YU',
  yu: 'YU',
  zacatecas: 'ZA',
  zac: 'ZA',
  za: 'ZA',
  zt: 'ZA',
};

/** Lower case; strip accents, dots, spaces and any ISO "MX-" prefix. */
function normalizeKey(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/^mx-/, '')
    .replace(/[^a-z]/g, '');
}

// Reverse of MX_STATES, for showing the applicant what the stored code means.
// "DF" in particular reads as wrong to anyone who typed "CDMX".
const MX_STATE_NAMES: Record<string, string> = {
  AG: 'Aguascalientes',
  BC: 'Baja California',
  BS: 'Baja California Sur',
  CM: 'Campeche',
  CS: 'Chiapas',
  CH: 'Chihuahua',
  CO: 'Coahuila',
  CL: 'Colima',
  DF: 'Ciudad de México (CDMX)',
  DG: 'Durango',
  GT: 'Guanajuato',
  GR: 'Guerrero',
  HG: 'Hidalgo',
  JA: 'Jalisco',
  MX: 'Estado de México',
  MI: 'Michoacán',
  MO: 'Morelos',
  NA: 'Nayarit',
  NL: 'Nuevo León',
  OA: 'Oaxaca',
  PU: 'Puebla',
  QT: 'Querétaro',
  QR: 'Quintana Roo',
  SL: 'San Luis Potosí',
  SI: 'Sinaloa',
  SO: 'Sonora',
  TB: 'Tabasco',
  TM: 'Tamaulipas',
  TL: 'Tlaxcala',
  VE: 'Veracruz',
  YU: 'Yucatán',
  ZA: 'Zacatecas',
};

/**
 * Human label for a stored 2-letter code, or '' when there is nothing useful to
 * add. Only Mexico is covered: US and CA codes are already self-explanatory.
 */
export function stateCodeLabel(
  code: string | undefined | null,
  country: string | undefined | null,
): string {
  if (!(country ?? '').trim().toUpperCase().startsWith('MEX')) return '';
  return MX_STATE_NAMES[(code ?? '').trim().toUpperCase()] ?? '';
}

/**
 * Resolves a state/province to the two-letter code the database stores.
 * Unrecognised values are returned unchanged so length validation surfaces the
 * problem, rather than silently storing a truncated, wrong code.
 */
export function toStateCode(value: string | undefined | null): string {
  const raw = (value ?? '').trim();
  if (!raw) return '';

  const key = raw.toLowerCase();
  const named = US_STATES[key] || CA_PROVINCES[key];
  if (named) return named;

  const mexican = MX_STATES[normalizeKey(raw)];
  if (mexican) return mexican;

  // Already a short code (no spaces): normalize case only.
  if (!/\s/.test(raw)) return raw.replace(/[^a-zA-Z]/g, '').toUpperCase();

  // An unrecognised full name: return it as-is rather than stripping spaces
  // and accents into a nonsense concatenation (e.g. "Ciudad de México" ->
  // "CIUDADDEMXICO"). Length validation then surfaces the mismatch visibly.
  return raw;
}
